import { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { TimePicker } from '@/components/ui/time-picker';
import { TimezonePicker } from '@/components/ui/timezone-picker';
import {
  LanguageScrollTabs,
  type Language,
} from '@/components/translations';
import { Bell } from 'lucide-react';
import type { LocalizedString } from './ScheduleEditor';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ScheduleNotificationSettingsValue {
  isEnabled: boolean;
  morningTime: string;
  reminderMinutesBefore: number;
  customDaySummaryMessage?: LocalizedString;
  customItemReminderMessage?: LocalizedString;
}

interface ScheduleNotificationSettingsProps {
  value: ScheduleNotificationSettingsValue | null;
  onChange: (settings: ScheduleNotificationSettingsValue) => void;
  timezone?: string;
  onTimezoneChange?: (tz: string) => void;
  languages?: Language[];
  activeLanguage?: string;
  onLanguageChange?: (code: string) => void;
}

// ─── Defaults ───────────────────────────────────────────────────────────────

const DEFAULT_SETTINGS: ScheduleNotificationSettingsValue = {
  isEnabled: false,
  morningTime: '08:00',
  reminderMinutesBefore: 10,
  customDaySummaryMessage: {},
  customItemReminderMessage: {},
};

// ─── Variable definitions ───────────────────────────────────────────────────

interface TemplateVariable {
  key: string;
  label: string;
}

const DAY_VARIABLES: TemplateVariable[] = [
  { key: '{{entityName}}', label: 'Название курса/события' },
  { key: '{{dayTitle}}', label: 'Название дня' },
  { key: '{{dayDescription}}', label: 'Описание дня' },
  { key: '{{scheduleList}}', label: 'Список пунктов дня' },
];

const ITEM_VARIABLES: TemplateVariable[] = [
  { key: '{{entityName}}', label: 'Название курса/события' },
  { key: '{{itemTitle}}', label: 'Название пункта' },
  { key: '{{itemTime}}', label: 'Время пункта' },
  { key: '{{minutes}}', label: 'Минут до начала' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function getLocalizedValue(value: LocalizedString | string | undefined, lang: string): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] ?? '';
}

function setLocalizedValue(current: LocalizedString | string | undefined, lang: string, text: string): LocalizedString {
  const base: LocalizedString = typeof current === 'string'
    ? { ru: current }
    : { ...(current ?? {}) };
  return { ...base, [lang]: text };
}

// ─── VariableTag ────────────────────────────────────────────────────────────

interface VariableTagProps {
  variable: TemplateVariable;
  onClick: () => void;
}

function VariableTag({ variable, onClick }: VariableTagProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md border border-dashed border-primary/40 bg-primary/5 px-2 py-0.5 text-xs font-mono text-primary transition-colors hover:bg-primary/10 hover:border-primary/60 cursor-pointer"
      title={`Вставить ${variable.key}`}
    >
      <span className="opacity-60">{'{{'}</span>
      {variable.key.replace(/\{\{|\}\}/g, '')}
      <span className="opacity-60">{'}}'}</span>
    </button>
  );
}

// ─── TemplateTextarea ───────────────────────────────────────────────────────

interface TemplateTextareaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  variables: TemplateVariable[];
}

function TemplateTextarea({ label, value, onChange, placeholder, variables }: TemplateTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertVariable = (key: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onChange(value + key);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const newValue = before + key + after;
    onChange(newValue);

    requestAnimationFrame(() => {
      textarea.focus();
      const cursorPos = start + key.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="min-h-0 font-mono text-sm"
      />
      <div className="flex flex-wrap gap-1.5 pt-0.5">
        {variables.map((v) => (
          <VariableTag
            key={v.key}
            variable={v}
            onClick={() => insertVariable(v.key)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ScheduleNotificationSettings({
  value,
  onChange,
  timezone,
  onTimezoneChange,
  languages = [],
  activeLanguage: externalLang,
  onLanguageChange: externalOnLangChange,
}: ScheduleNotificationSettingsProps) {
  const [internalLang, setInternalLang] = useState('ru');
  const lang = externalLang ?? internalLang;
  const setLang = externalOnLangChange ?? setInternalLang;

  const settings = value ?? DEFAULT_SETTINGS;

  const update = (updates: Partial<ScheduleNotificationSettingsValue>) => {
    onChange({ ...settings, ...updates });
  };

  const updateLocalizedField = (
    field: 'customDaySummaryMessage' | 'customItemReminderMessage',
    text: string,
  ) => {
    update({
      [field]: setLocalizedValue(settings[field], lang, text),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Уведомления о расписании
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Включить уведомления</p>
            <p className="text-xs text-muted-foreground">
              Участники получат уведомления о расписании
            </p>
          </div>
          <Switch
            checked={settings.isEnabled}
            onCheckedChange={(checked) => update({ isEnabled: checked })}
          />
        </div>

        {settings.isEnabled && (
          <div className="space-y-4 pt-2 border-t">
            {/* Timezone */}
            {onTimezoneChange && (
              <div className="space-y-1.5">
                <Label className="text-sm">Часовой пояс</Label>
                <TimezonePicker
                  value={timezone}
                  onChange={onTimezoneChange}
                  className="w-72"
                />
              </div>
            )}

            {/* Morning time */}
            <div className="space-y-1.5">
              <Label className="text-sm">Время утренней рассылки</Label>
              <TimePicker
                value={settings.morningTime}
                onChange={(time) => update({ morningTime: time })}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                Время отправки утреннего сообщения с расписанием дня
              </p>
            </div>

            {/* Reminder minutes */}
            <div className="space-y-1.5">
              <Label className="text-sm">Напоминание за N минут до пункта</Label>
              <Input
                type="number"
                min={1}
                max={60}
                value={settings.reminderMinutesBefore}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!Number.isNaN(val) && val >= 1 && val <= 60) {
                    update({ reminderMinutesBefore: val });
                  }
                }}
                className="w-24"
              />
              <p className="text-xs text-muted-foreground">
                За сколько минут до начала пункта отправить напоминание (1-60)
              </p>
            </div>

            {/* Language tabs for templates */}
            {languages.length > 1 && (
              <div className="pt-2 border-t">
                <Label className="text-sm mb-2 block">Шаблоны сообщений по языкам</Label>
                <LanguageScrollTabs
                  languages={languages}
                  activeLanguage={lang}
                  onLanguageChange={setLang}
                  size="sm"
                  showLanguageSelector={false}
                />
              </div>
            )}

            {/* Custom day summary message */}
            <TemplateTextarea
              label="Шаблон утреннего сообщения (необязательно)"
              value={getLocalizedValue(settings.customDaySummaryMessage, lang)}
              onChange={(v) => updateLocalizedField('customDaySummaryMessage', v)}
              placeholder="Доброе утро! Сегодня у нас запланировано: {{scheduleList}}"
              variables={DAY_VARIABLES}
            />

            {/* Custom item reminder message */}
            <TemplateTextarea
              label="Шаблон напоминания о пункте (необязательно)"
              value={getLocalizedValue(settings.customItemReminderMessage, lang)}
              onChange={(v) => updateLocalizedField('customItemReminderMessage', v)}
              placeholder="Через {{minutes}} мин начнётся: {{itemTitle}}"
              variables={ITEM_VARIABLES}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
